import { IUserGroupDetails } from "@Models/IUserGroupDetails";
import { getSystemId, hasCapability } from "@Utils";
import { TagType } from "@Components/groups/TagProperties";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { GroupContext, QueryKeys } from "@Constants";
import { QueryKeys as GroupsQueryKeys } from "@Features/groups/constants";
import UserGroupService from "@Services/UserGroupService";
import { notification } from "antd";
import { IDeleteAction } from "./types";

export interface IGroupEntry {
    groupId?: string;
    parentGroupId: string;
    name: string;
    license?: string;
    level?: number;
    children?: string[];
    visibility?: boolean;
    page?: number;
    capacity?: number;
    count: number;
}

export interface INavState {
    currentLevel: number;
    currentGroupId?: string;
}

export interface IListState {
    groups?: IGroupEntry[];
}

export interface ITag {
    label: string;
    type: TagType;
    instanceId: string;
    enableDelete: boolean;
    context: number;
    entityId: string;
    groupId: string;
    page: number;
    userId: string;
    deleteAction: IDeleteAction;
}
export interface ITagMinimal {
    label: string;
    type: TagType;
    groupId: string;
    page: number;
    userId: string;
}

export interface ICascaderOptions {
    label: string;
    value: string;
    children?: ICascaderOptions[];
}

/**
 * Build up the entire list of group entries based on the user's available root groups.
 * @param navState
 * @param groups
 */
export const buildGroupList = (navState: INavState, groups: IUserGroupDetails[]): IListState => {
    const allGroups: IUserGroupDetails[] = [...groups];

    // Build the children list for each group
    groups.forEach((group, index) => {
        allGroups[index].children = [];
        groups
            .filter((x) => x.parentGroupId === group.groupId)
            .forEach((g) => {
                allGroups[index].children.push(g.groupId);
            });
    });

    const rootGroups: IUserGroupDetails[] = [];
    allGroups
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((group) => {
            if (group !== undefined && hasCapability("UserGroup/Get", 2, group.groupId)) {
                if (allGroups.find((x) => x.groupId == group.parentGroupId) === undefined) {
                    rootGroups.push(group);
                }
            }
        });

    let listState: IListState = { groups: [] };
    if (rootGroups.length === 0) return listState;
    rootGroups.forEach((rootGroup) => {
        listState = addGroupsRecursive(listState, navState, allGroups, rootGroup, 0);
    });
    return listState;
};

/**
 * Add group entries into the flat group entries array in the list state, including nested child groups.
 * @param state
 * @param navState
 * @param allGroups
 * @param group
 * @param level
 */
const addGroupsRecursive = (
    state: IListState,
    navState: INavState,
    allGroups: IUserGroupDetails[],
    group: IUserGroupDetails,
    level: number
) => {
    state = addGroupEntry(state, navState, group, level);
    level++;
    group.children.forEach((childId) => {
        state = addGroupsRecursive(
            state,
            navState,
            allGroups,
            allGroups.find((x) => x.groupId === childId),
            level
        );
    });
    return state;
};

/**
 * Add a group entry to the list state.
 * @param state
 * @param navState
 * @param model
 * @param level
 */
export const addGroupEntry = (state: IListState, navState: INavState, model: IUserGroupDetails, level = 0) => {
    const groupIndex = state.groups.findIndex((x) => x.groupId === model.groupId);
    // @ts-expect-error // TODO: Here until using strict mode
    const newGroup: IGroupEntry = { ...model };
    newGroup.level = level;
    newGroup.visibility = model.groupId === navState.currentGroupId;
    newGroup.page = 0;
    if (groupIndex === -1) {
        state.groups.push(newGroup);
    } else {
        state.groups[groupIndex] = newGroup;
    }
    return state;
};

/**
 * Remove a group entry from the list state.
 * @param state
 * @param groupId
 */
export const removeGroupEntry = (state: IListState, groupId: string) => {
    state.groups = state.groups.filter((x) => x.groupId !== groupId);
    return state;
};

/**
 * Get a group entry object from the list state.
 * @param listState
 * @param groupId
 */
export const getListStateGroup = (listState: IListState, groupId: string) => {
    const index = listState.groups.findIndex((x) => x.groupId === groupId);
    return index > -1 ? listState.groups[index] : null;
};

/**
 * Set the current group id in the nav state.
 */
export const updateCurrentGroup = (state: INavState, groupId: string) => {
    state.currentGroupId = groupId;
    return state;
};

/**
 * Get all groups where capabilities allow and the user is not already a member of.
 */
export const getGroupOptions = (allGroups: IUserGroupDetails[] = []): ICascaderOptions[] => {
    const candidates = allGroups.filter((x) => x.parentGroupId === getSystemId());
    const options: ICascaderOptions[] = [];
    candidates?.forEach((x) => {
        if (hasCapability("UserGroup/AddMembers", GroupContext, x?.groupId)) {
            options.push({
                value: x?.groupId,
                label: x?.name,
                children: getGroupOptionsRecursive(allGroups, x),
            });
        }
    });
    return options;
};

/**
 * Return the group options list recursively determined child groups.
 * @param allGroups
 * @param group
 */
export const getGroupOptionsRecursive = (allGroups: IUserGroupDetails[], group: IUserGroupDetails) => {
    const groupOptions: ICascaderOptions[] = [];
    group.children?.forEach((x) => {
        const child = allGroups?.find((y) => y.groupId === x);
        if (hasCapability("UserGroup/AddMembers", GroupContext, child?.groupId)) {
            groupOptions.push({
                value: child?.groupId,
                label: child?.name,
                children: getGroupOptionsRecursive(allGroups, child),
            });
        }
    });
    return groupOptions;
};

export const useGroupAddMembers = (groupId: string) => {
    const service = new UserGroupService();
    const queryClient = useQueryClient();

    return useMutation((userIds: string[]) => service.addMembers(groupId, userIds), {
        onSuccess: async (res) => {
            if (!res.hasErrors) {
                notification.success({ message: "Added to group." });
                await queryClient.invalidateQueries([QueryKeys.UserGroups]);
                await queryClient.invalidateQueries([QueryKeys.GroupMembers, groupId]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, groupId]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserAddresses, groupId]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserCredits, groupId]);

                await queryClient.invalidateQueries([GroupsQueryKeys.GroupsList]);
                await queryClient.invalidateQueries([GroupsQueryKeys.GroupUsers]);
            } else {
                notification.error({ message: "Error adding to group." });
            }
        },
    });
};

export const useGroupRemoveMembers = (groupId: string) => {
    const service = new UserGroupService();
    const queryClient = useQueryClient();

    return useMutation((userIds: string[]) => service.removeMembers(userIds, groupId), {
        onSuccess: async (res) => {
            if (!res.hasErrors) {
                notification.success({ message: "Removed from group." });
                await queryClient.invalidateQueries([QueryKeys.UserGroups]);
                await queryClient.invalidateQueries([QueryKeys.GroupMembers, groupId]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, groupId]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserAddresses, groupId]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserCredits, groupId]);
            } else {
                notification.error({ message: "Error removing from group." });
            }
        },
    });
};
